import {useRoute, useRouter} from '#imports';

export const TrackingSources = ['direct', 'email', 'project_list', 'project_view', 'project_action', 'quote'] as const;
// Any `trackingSource` query param value is also accepted, so other apps can set their own source
export type TrackingSource = (typeof TrackingSources)[number] | (string & {});

/**
 * @param previousRouteName Name of the in-app route the user comes from, `undefined` when landing from outside the app
 */
export function trackingSourceFromPreviousRouteName(previousRouteName: unknown): TrackingSource | undefined {
    if (typeof previousRouteName !== 'string') {
        return undefined;
    }
    if (previousRouteName.startsWith('project-projectId-index')) {
        return 'project_view';
    }
    if (previousRouteName.startsWith('project-projectId')) {
        // validation, expense validation, contract amendment... funnels opened from the project view
        return 'project_action';
    }
    if (previousRouteName === 'index') {
        return 'project_list';
    }
    if (previousRouteName.startsWith('quote')) {
        return 'quote';
    }
    return undefined;
}

/**
 * Source of a page view, by priority:
 * 1. `trackingSource` query param, set by the page linking to the viewed page (any value)
 * 2. `email` when coming from an email (`utm_medium=email`)
 * 3. previous in-app page
 * 4. `direct`
 */
export function landingTrackingSource(query: {trackingSource?: unknown; utm_medium?: unknown}, previousRouteName: unknown): TrackingSource {
    if (typeof query.trackingSource === 'string' && query.trackingSource) {
        return query.trackingSource;
    }
    if (query.utm_medium === 'email') {
        return 'email';
    }
    return trackingSourceFromPreviousRouteName(previousRouteName) ?? 'direct';
}

export function useLandingTrackingSource() {
    const route = useRoute();
    const router = useRouter();

    function getLandingTrackingSource(): TrackingSource {
        // `back` is only set when the user navigated within the app
        const back = router.options.history.state.back;
        const previousRouteName = typeof back === 'string' ? router.resolve(back).name : undefined;
        return landingTrackingSource(route.query, previousRouteName);
    }

    return {getLandingTrackingSource};
}
